import { MetricUnit } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { type FC } from 'react';
import {
  AU_SLIDER_SX,
  OC_DEFINE_SCALE_SX,
  SLIDER_TONE_COLOR,
  type PositionTone,
} from '@/theme/au-compat';

interface Props {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number | undefined;
  /** El estado del formulario, que tiñe el indicador. `.oc-define-slider` reasigna `--au-primary`. */
  tone: PositionTone;
}

/**
 * El slider de posición y la escala de extremos: `.oc-define-slider` + `.oc-define-scale` del
 * espécimen DPG `55fd83a`.
 *
 * `defaultValue` no viaja como prop: el slider es controlado y el valor de reposo lo aporta el
 * tono, que ya distingue idle de dirty. Y el mensaje de error tampoco: acá sólo interesa el tono,
 * y el texto lo pinta `.oc-define-error` en el formulario.
 */
export const PositionSlider: FC<Props> = ({ value, onChange, min, max, tone }) => {
  const { formatMetricValue } = useFormatters();

  return (
    <Box>
      <Slider
        valueLabelDisplay="off"
        value={value}
        onChange={(_, value) => {
          if (typeof value === 'number') onChange(value);
        }}
        min={min}
        max={max}
        sx={{ ...AU_SLIDER_SX, color: SLIDER_TONE_COLOR[tone] }}
      />
      <Box sx={OC_DEFINE_SCALE_SX}>
        <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>
          {formatMetricValue(min, { unit: MetricUnit.Centimeter, precision: 0 })}
        </Typography>
        {max != null && (
          <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>
            {formatMetricValue(max, { unit: MetricUnit.Centimeter, precision: 0 })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
